import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import dayjs from 'dayjs';
import styled from 'styled-components';

import { TABLE_ACTIONS } from 'common/Table/constants';
import { StyledTable } from 'common/Table/StyledTable';
import { ActionPlanListItemType } from 'types/actionPlans';

import { ActionPlanActionsCell, ActionPlanTitleCell } from './ActionPlansTableRenderers';
import ActionPlanStatusSelector from './ActionPlanStatusSelector';
import { ACTION_PLAN_STATUS_BG_COLOR_MAP, ACTION_PLAN_STATUS_OPTIONS, ACTION_PLANS_PAGE_SIZE } from './constants';
import FocusAreaTag from './FocusAreaTag';
import OwnerInfo from './OwnerInfo';

const TableWrapper = styled.div`
  background: var(--white);
  border-radius: 8px;
  padding: 8px 0;

  .ant-table-thead > tr > th {
    border-color: #d7d7d7 !important;
  }

  .ant-table-thead > tr > th:first-child {
    padding-left: 25px !important;
  }

  .ant-table-thead > tr > th.ant-table-cell::before {
    display: none;
  }

  .ant-table-tbody > tr > td {
    border-color: var(--line-gray) !important;
    height: 60px;
    vertical-align: middle;
  }

  .ant-table-tbody > tr > td:first-child {
    border-left: 1px solid var(--line-gray);
    padding-left: 20px;
  }

  .ant-table-tbody > tr > td:last-child {
    border-right: 1px solid var(--line-gray);
  }
`;

const FocusAreaCell = styled.span`
  display: inline-block;
`;

const PLAN_TITLE_COLUMN_WIDTH = 360;
const FOCUS_AREA_COLUMN_WIDTH = 170;
const OWNER_COLUMN_WIDTH = 170;
const DUE_DATE_COLUMN_WIDTH = 130;
const STATUS_COLUMN_WIDTH = 160;
const ACTIONS_COLUMN_WIDTH = 160;

const renderPaginationTotal = (total: number, range: number[]) => {
  const displayedItems = range[1] - range[0] + 1;
  return (
    <span>
      Showing {displayedItems} of {total} records
    </span>
  );
};

type Props = {
  plans: ActionPlanListItemType[];
  loading: boolean;
  page: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  currentUserDisplayName: string;
  focusAreaLabelById: Record<number, string>;
  hasCurrentUser: boolean;
  selectedCommunityId?: number;
  showRelatedAlert: boolean;
  onAssignToMe: (planId: number) => Promise<unknown>;
  onUpdateStatus: (args: { actionPlanId: number; status: number }) => Promise<unknown>;
  onEditPlan: (plan: ActionPlanListItemType) => void;
  onDeletePlan: (plan: ActionPlanListItemType) => void;
};

const ActionPlansTable = ({
  plans,
  loading,
  page,
  totalCount,
  onPageChange,
  currentUserDisplayName,
  focusAreaLabelById,
  hasCurrentUser,
  selectedCommunityId,
  showRelatedAlert,
  onAssignToMe,
  onUpdateStatus,
  onEditPlan,
  onDeletePlan,
}: Props) => {
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        title: 'PLAN TITLE',
        dataIndex: 'name',
        key: 'name',
        width: PLAN_TITLE_COLUMN_WIDTH,
        render: (value: ActionPlanListItemType['name'], record: ActionPlanListItemType) => (
          <ActionPlanTitleCell
            title={value}
            plan={record}
            fallbackUserLabel={currentUserDisplayName}
            onViewPlan={(planId) => navigate(`${planId}`)}
          />
        ),
      },
      {
        title: 'FOCUS AREA',
        dataIndex: 'focus_area_id',
        key: 'focus_area_id',
        width: FOCUS_AREA_COLUMN_WIDTH,
        render: (value: ActionPlanListItemType['focus_area_id']) => {
          const label = focusAreaLabelById[value];
          return label ? (
            <FocusAreaCell>
              <FocusAreaTag label={label} />
            </FocusAreaCell>
          ) : (
            '-'
          );
        },
      },
      {
        title: 'OWNER',
        dataIndex: 'owner',
        key: 'owner',
        width: OWNER_COLUMN_WIDTH,
        render: (_value: ActionPlanListItemType['owner'], record: ActionPlanListItemType) => (
          <OwnerInfo
            name={record.owner?.name ?? null}
            avatarUrl={record.owner?.avatar_url ?? null}
            showAssignToMe={!record.owner && hasCurrentUser}
            onAssignToMe={async () => {
              await onAssignToMe(record.id);
            }}
          />
        ),
      },
      {
        title: 'DUE DATE',
        dataIndex: 'due_date',
        key: 'due_date',
        width: DUE_DATE_COLUMN_WIDTH,
        render: (value: ActionPlanListItemType['due_date']) => (value ? dayjs(value).format('MMM D, YYYY') : '-'),
      },
      {
        title: 'STATUS',
        dataIndex: 'status',
        key: 'status',
        width: STATUS_COLUMN_WIDTH,
        render: (value: ActionPlanListItemType['status'], record: ActionPlanListItemType) => (
          <ActionPlanStatusSelector
            value={value}
            options={ACTION_PLAN_STATUS_OPTIONS}
            statusColorMap={ACTION_PLAN_STATUS_BG_COLOR_MAP}
            dataTestId="action-plans-status-selector"
            onChange={async (status) => {
              if (status === value) {
                return;
              }
              await onUpdateStatus({ actionPlanId: record.id, status });
            }}
          />
        ),
      },
      {
        title: '',
        key: 'actions',
        width: ACTIONS_COLUMN_WIDTH,
        render: (_value: unknown, record: ActionPlanListItemType) => (
          <ActionPlanActionsCell
            plan={record}
            selectedCommunityId={selectedCommunityId}
            showRelatedAlert={showRelatedAlert}
            onViewPlan={(planId) => navigate(`${planId}`)}
            onEditPlan={onEditPlan}
            onDeletePlan={onDeletePlan}
            onViewAlert={(communityId, alertId) => navigate(`/community/${communityId}/alerts/${alertId}`)}
          />
        ),
      },
    ],
    [
      currentUserDisplayName,
      focusAreaLabelById,
      hasCurrentUser,
      navigate,
      onAssignToMe,
      onDeletePlan,
      onEditPlan,
      onUpdateStatus,
      selectedCommunityId,
      showRelatedAlert,
    ]
  );

  return (
    <TableWrapper data-test-id="action-plans-table-wrapper">
      <div data-test-id="action-plans-table">
        <StyledTable
          columns={columns as any}
          size={'small'}
          dataSource={plans}
          bordered={false}
          loading={loading}
          rowKey="id"
          onChange={(pagination: any, _filters: any, _sorter: any, extra: any) => {
            if (extra?.action === TABLE_ACTIONS.PAGINATE) {
              onPageChange(pagination.current);
            }
          }}
          pagination={{
            size: 'small',
            position: ['bottomLeft'],
            showSizeChanger: false,
            current: page,
            pageSize: ACTION_PLANS_PAGE_SIZE,
            total: totalCount,
            showTotal: renderPaginationTotal,
          }}
        />
      </div>
    </TableWrapper>
  );
};

export default ActionPlansTable;
